<script>
import MottoIcon from '@components/motto-icon.vue'
import FavoriteHeart from '@components/favorite-heart.vue'
import LoaderIcon from '@components/loader-icon.vue'
import { addFavoriteEvent, deleteFavoriteEvent } from '@src/api/events'
import { eventsComputed } from '@state/helpers'
import { eventColorsMixin } from '@src/mixins/eventColorsMixin'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import { getDateTimeTexts } from '@utils/date-helpers'
import moment from 'moment'

export default {
  components: {
    MottoIcon,
    FavoriteHeart,
    LoaderIcon,
  },
  mixins: [eventColorsMixin, isMobileMixin],

  props: {
    event: {
      type: Object,
      required: true,
    },
    minimalDesign: {
      type: Boolean,
    },
    currentDate: {
      type: [Date, null],
      default: null,
    },
  },
  data() {
    return {
      isTransparent: false,
      loadingFavorite: false,
    }
  },
  computed: {
    ...eventsComputed,
    getStartTimeTexts() {
      return getDateTimeTexts(moment(this.event.start, 'YYYY-MM-DD hh:mm:ss').toDate())
    },

    getEndTimeTexts() {
      return getDateTimeTexts(moment(this.event.end, 'YYYY-MM-DD hh:mm:ss').toDate())
    },

    getCurrentDateTimeTexts() {
      return getDateTimeTexts(moment(this.currentDate).toDate())
    },

    dateInfoText() {
      if (!this.currentDate) {
        return this.getStartTimeTexts
      }

      return this.getDaysDiff(this.currentDate, this.event.start) > 1
        ? this.getCurrentDateTimeTexts
        : this.getStartTimeTexts
    },

    isFavorite() {
      return this.isEventFavorite(this.event.eventId)
    },
  },

  methods: {
    async handleHeartClick() {
      if (this.loadingFavorite) {
        return
      }

      this.loadingFavorite = true
      if (this.isFavorite) {
        await this.unfavoriteEvent()
      } else {
        await this.favoriteEvent()
      }
      this.loadingFavorite = false
    },

    async favoriteEvent() {
      await addFavoriteEvent({ eventId: this.event.eventId })
      this.$store.commit('events/FAVORITE_EVENT_BY_ID', this.event.eventId)
    },

    async unfavoriteEvent() {
      await deleteFavoriteEvent({ eventId: this.event.eventId })
      this.$store.commit('events/UNFAVORITE_EVENT_BY_ID', this.event.eventId)
    },

    viewEvent() {
      if (this.isMobile) {
        this.$router.push({
          name: 'event',
          params: {
            eventId: this.event.eventId.toString(),
          },
        })
        return
      }

      this.$store.commit('events/SET_SELECTED_EVENT_ID', this.event.eventId)
      this.$bvModal.show('view-event-modal')
    },

    clickEventBlock() {
      if (this.minimalDesign) {
        this.viewEvent()
      }
    },

    getDaysDiff(date1, date2) {
      return moment(date1, 'YYYY-MM-DD hh:mm:ss').diff(moment(date2, 'YYYY-MM-DD hh:mm:ss').startOf('day'), 'days') + 1
    },
  },
}
</script>

<template>
  <div
    :class="{
      [$style.eventBlock]: true,
      [$style.minimalDesign]: minimalDesign,
      'light-transparent': isTransparent && minimalDesign,
    }"
    data-event-block
    @mouseover="isTransparent = true"
    @mouseout="isTransparent = false"
    @click="clickEventBlock">
    <LoaderIcon v-if="loadingFavorite" class="d-inline" :class="$style.heartIcon" />
    <FavoriteHeart
      v-else-if="event.eventId"
      :class="$style.heartIcon"
      :is-favorite="isFavorite"
      @handleHeartClick="handleHeartClick" />
    <div
      :class="$style.dateInfo"
      :style="{
        border: `1px solid ${getEventColorHex(event.eventType)}`,
      }">
      <p :class="$style.month" :style="{ color: `${getEventColorHex(event.eventType)}` }">
        {{ dateInfoText.month.toUpperCase() }}
      </p>
      <h1 :class="$style.dayNumber" :style="{ color: `${getEventColorHex(event.eventType)}` }" data-event-day>{{
        dateInfoText.day
      }}</h1>
      <h4 :class="$style.dayText" :style="{ color: `${getEventColorHex(event.eventType)}` }">
        {{ dateInfoText.weekday.toUpperCase() }}
      </h4>
      <p
        v-if="event.multiDay"
        :class="$style.multidayText"
        :style="{ color: `${getEventColorHex(event.eventType)}` }"
        class="p2">
        DAY {{ getDaysDiff(currentDate, event.start) > 0 ? getDaysDiff(currentDate, event.start) : 1 }}
      </p>
      <p :class="$style.time" :style="{ color: `${getEventColorHex(event.eventType)}` }" class="p2">
        {{ event.allDay ? 'ALL DAY' : getStartTimeTexts.time }}
      </p>
      <template v-if="!event.allDay && getEndTimeTexts.time">
        <p :class="$style.time" class="p2" :style="{ color: `${getEventColorHex(event.eventType)}` }">To</p>
        <p :class="$style.time" :style="{ color: `${getEventColorHex(event.eventType)}` }" class="p2">
          {{ getEndTimeTexts.time }}
        </p>
      </template>
    </div>
    <div :class="$style.eventInfo">
      <p :class="$style.eventTitle" class="tag-text">{{ event.title.toUpperCase() }}</p>
      <p v-if="event.multiDay" :class="$style.eventDateRangeText" class="tag-text">
        {{ getStartTimeTexts.weekday }}, {{ getStartTimeTexts.monthShort }} {{ getStartTimeTexts.day }} -
        {{ getEndTimeTexts.weekday }}, {{ getEndTimeTexts.monthShort }} {{ getEndTimeTexts.day }}
      </p>
      <p :class="$style.eventDescription" class="p2">{{ event.descriptionTruncated }}</p>
      <div v-if="event.location" :class="$style.locationDetails">
        <MottoIcon name="Place"></MottoIcon>
        <p class="p2">{{ event.location }}</p>
      </div>
      <p :class="$style.learnMoreText" class="button-text" @click="viewEvent">Learn More</p>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.eventBlock {
  position: relative;
  display: flex;
  background: $white;
  border: 1px solid $lightest-gray;
  border-radius: 8px;
  box-shadow: 3px 3px 6px rgba(196, 196, 196, 0.3);
}

.heartIcon {
  position: absolute;
  top: 10px;
  right: 15px;
}

.dateInfo {
  @media (max-width: $lg-breakpoint) {
    flex: 1 1 auto;
  }

  flex: 0 0 85px;
  width: 85px;
  max-width: 85px;
  padding: 16px 16px 12px 16px;
  margin: 3px 0 3px 3px;
  text-align: center;
  background: $lightest-gray;
  border-radius: 7px;
}

.month {
  font-size: 10px;
  font-weight: 400;
  color: $gray;
}

.dayNumber {
  margin-top: 8px;
  margin-bottom: 0;
  color: $primary-blue;
}

.dayText {
  margin-bottom: 6px;
  font-weight: 700;
  color: $primary-blue;
}

.multidayText {
  margin-bottom: 6px;
  font-weight: 700;
  white-space: nowrap;
}

.time {
  color: $dark-gray;
  white-space: nowrap;
}

.eventInfo {
  @media (max-width: $lg-breakpoint) {
    height: 100%;
  }

  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 16px;
}

.eventTitle {
  padding-right: 20px;
  margin-top: 4px;
  margin-bottom: 8px;
  font-size: 14px;
  color: $dark-gray;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.eventDateRangeText {
  color: $dark-gray;
}

.eventDescription {
  margin-top: 8px;
  margin-bottom: 12px;
  color: $dark-gray;
  white-space: break-spaces;
}

.locationDetails {
  display: flex;
  align-items: center;

  svg {
    margin-right: 9px;
  }

  p {
    color: $darkest-gray;
    word-break: break-all;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}

.learnMoreText {
  margin-top: auto;
  color: $primary-blue;
  text-align: right;
  text-decoration: underline;
  cursor: pointer;
}

.minimalDesign {
  flex-direction: column;
  cursor: pointer;

  .dateInfo {
    width: auto;
    max-width: unset;
    padding: 8px 8px 16px 8px;
    margin: 3px;
  }

  .eventInfo {
    width: auto;
  }

  .eventTitle {
    padding: 0;
    margin-top: 0;
    font-size: 12px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .eventDescription {
    display: none;
  }

  .locationDetails {
    display: none;
  }

  .learnMoreText {
    display: none;
  }

  .month {
    text-align: left;
  }

  .time {
    display: none;
  }

  .multidayText {
    display: none;
  }

  .eventDateRangeText {
    display: none;
  }

  .dayText {
    margin-bottom: 0;
  }
}
</style>
